import { Injectable } from '@angular/core';
import { AlertController, NavController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { TranslationService } from './translation.service';
import { WorkosService } from './workos.service';
import { Events } from './Events'
import { SlackService } from './slack.service'

@Injectable({
  providedIn: 'root'
})
export class ErrorManagerServiceService {

  protected translations = {
      general_error_title: '',
      general_error_subtitle: '',
      error_profile_no_access: '',
      outDatedAlertTitle: '',
      outDatedAlertSubHeader: '',
      outDatedAlertMessage: '',
      gateWayErrorTitle: '',
      gateWayAlertSubtitle: '',
      gateWayAlertMessage: '',
      api_error_door_failure: '',
      api_error_no_access: '',
      api_error_gate_unknown: '',
      bad_configuration_timetable: '',
      no_spot_left: '',
      invalid_timespan: '',
      no_time_available: '',
      time_spot_slot_exist_in_timeframe: '',
      no_credit: '',
      not_enough_credit: '',
      no_wallet: '',
      time_spot_slot_outside_day_limit: '',
      check_position_error_too_far: '',
      check_position_gps_coordinate_invalid: '',
      validationErrorTitle: '',
      end_date_time_required_must_refresh: '',
      access_sequence_not_respected: '',
      error_401_title: '',
      error_401_message: '',
  };

  managedErrorCodes = [
    301, // Moved Permanently
    400, // Bad Request
    401, // Unauthorized
    422, // Unprocessable Entity
    504, // Gateway Timeout
  ];

  private isHandling401 = false;

  constructor(
    private apiService: ApiService,
    private alertController: AlertController,
    private storage: StorageService,
    private navController: NavController,
    private translationService: TranslationService,
    private workosService: WorkosService,
    private events: Events,
    private slackService: SlackService
  ) {
    translationService.loadTranslation('error', this.translations);
  }

  public handle(error, displayInAlert: boolean = true) {

    switch (error.status) {
        case 301:
            this.manage301Error(error);
            break;
        case 400:
            const manager400 = this.manage400Error(error, displayInAlert);

            if (displayInAlert) {
                return manager400;
            }

            break;
        case 401:
            this.manage401Error(error);
            break;
        case 422:
            this.manage422Error(error);
            break;
        case 504:
            this.manage504Error(error);
            break;
        default:
            this.manageUnknownError(error);
    }
  }

  protected manage400Error(error, displayInAlert) {
    let message: string;
    let errorMessage;

    if(error.error.version === 1)
    {
        const errorData = error.error.data;

        // In next version of the api, error_code will be removed and replaced by message key.
        // This piece of code allow the app to work with both version.
        if (errorData.message !== undefined) {
            errorMessage = errorData.message;
        } else if (errorData.error_code !== undefined) {
            errorMessage = errorData.error_code;
        }
    }
    else if(error.error.version === 2)
    {
        errorMessage = error.error.error_description;
    }
    else
    {
        errorMessage = JSON.stringify(error.error);
    }

    switch (errorMessage) {
        case 'PROFILE_NOT_ALLOWED':
            message = this.translations.error_profile_no_access;
            break;
        case 'ERROR_REACHING_DOOR':
            message = this.translations.api_error_door_failure;
            break;
        case 'ERROR_OUT_OF_ACCESS_HOUR':
            message = this.translations.api_error_no_access;
            break;
        case 'ERROR_UNKNOWN_GATE_KIND':
            message = this.translations.api_error_gate_unknown;
            break;
        case 'BAD_CONFIGURATION_TIMETABLE':
            message = this.translations.bad_configuration_timetable;
            break;
        case 'no_spot_left':
            message = this.translations.no_spot_left;
            break;
        case 'invalid_timespan':
            message = this.translations.invalid_timespan;
            break;
        case 'no_time_available':
            message = this.translations.no_time_available;
            break;
        case 'no_credit':
            message = this.translations.no_credit;
            break;
        case 'not_enough_credit':
            message = this.translations.not_enough_credit;
            break;
        case 'no_wallet':
            message = this.translations.no_wallet;
            break;
        case 'time_spot_slot_exist_in_timeframe':
            message = this.translations.time_spot_slot_exist_in_timeframe;
            break;
        case 'time_spot_slot_outside_day_limit':
            message =  this.translations.time_spot_slot_outside_day_limit;
            break;
        case 'gps_coordinate_invalid':
            message = this.translations.check_position_gps_coordinate_invalid;
            break;
        case 'too_far_from_access_point':
            message = this.translations.check_position_error_too_far;
            break;
        case 'The end date time is required to open an On the fly booking access the first time':
            message = this.translations.end_date_time_required_must_refresh;
            break;
        case 'access_sequence_not_respected':
            message = this.translations.access_sequence_not_respected;
            break;
      default:
          message = error.error.data.error_code;
    }

    if (displayInAlert) {
      this.presentAlert(message);
    } else {
      return message;
    }
  }

  // We need to make a local implementation of the logout function because
  // the logout function in the login service cannot be imported here
  // due to circular dependency issues.
  private logout = () => {
    this.storage.cleanOAuth();
    this.workosService.signOut();
    this.events.destroy('reload_access');
    this.events.destroy('reload_bookings');
    this.events.destroy('reload_profile');
  }

  protected async manage401Error(error) {
    if (this.isHandling401) return;
    this.isHandling401 = true;

    if (!environment.production) {
        // Wait 3 sec before continuing to let the dev debug in a dev environment
        console.error("ERROR 401, signing out - ", error);
        await new Promise(resolve => setTimeout(resolve, 3000));
    }

    try {
      const user = await this.workosService.getUser();
      await this.slackService.postError(error, 'Logout after unauthorized access', user, 'critical');
      await this.presentAlert(this.translations.error_401_message, this.translations.error_401_title);
      this.logout();
    } finally{
      this.isHandling401 = false;
    }
  }

  protected async manage301Error(error) {
      if (error.error.data.error_code === 'OUTDATED_APP') {
        await this.presentAlert(this.translations.outDatedAlertMessage, this.translations.outDatedAlertTitle, this.translations.outDatedAlertSubHeader);

        if (!environment.production){
            console.error("ERROR 301, signing out - ", error);
            await new Promise(resolve => setTimeout(resolve, 3000));
        }


        const user = await this.workosService.getUser();
        await this.slackService.postError(error, 'Logout after app outdated', user, 'critical');
        this.logout();
      }
  }

  protected manage422Error(error) {
        this.presentAlert( error.error.errors.plate[0], this.translations.validationErrorTitle);
  }

  protected manage504Error(error) {
      this.presentAlert(this.translations.gateWayAlertMessage, this.translations.gateWayErrorTitle, this.translations.gateWayAlertSubtitle);
  }

  protected manageUnknownError(error) {
      // alert('An uncatched call to the api has failed with message : ' + error.message);
      console.error("An API call failed with an unknown error. ", error)
  }

  async presentAlert(message, header: string = null, subheader: string = null) {

    if (header === null) {
        header = this.translations.general_error_title;
    }

    if (subheader === null) {
        subheader = this.translations.general_error_subtitle;
    }

    const alert = await this.alertController.create({
        header: header,
        subHeader: subheader,
        message: message,
        buttons: [
            {
                text: 'OK',
            }
        ]
    });

    await alert.present();
    return alert.onDidDismiss();
  }
}
